<template>
    <div class="pillar-charts-container">
        <!-- 排名堆叠条 -->
        <template v-if="rankList && rankList.length > 0">
            <div class="chart-section">
                <div class="stacked-bar-wrapper">
                    <div class="stacked-bar" @mouseleave="activeIndex = null">
                        <div v-for="(rank, index) in rankList" :key="'rank-' + index" class="bar-segment"
                            :class="{ active: activeIndex === index }"
                            :style="getSegmentStyle(rank, totalRank, scoreColors[index])"
                            @mouseenter="activeIndex = index">
                            <span class="segment-label" v-if="getSegmentWidth(rank, totalRank) > 10">
                                {{ rank }}/{{totalList[index] || 100}}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- 悬浮提示框 -->
                <transition name="fade">
                    <div v-if="activeIndex !== null" class="tooltip">
                        <div class="tooltip-content">
                            <table>
                                <tbody>
                                    <tr v-for="(name, idx) in nameList" :key="'ranktip-' + idx">
                                        <td>
                                            <span class="color-dot"
                                                :style="{ backgroundColor: scoreColors[idx] }"></span>
                                            {{ name }}
                                        </td>
                                        <td style="padding-left: 16px;">
                                            <span :class="{ highlight: idx === activeIndex }">{{ rankList[idx] }}/{{totalList[idx] || 100}}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>
            </div>
        </template>

        <!-- 得分堆叠条 -->
        <template v-if="scoreList && scoreList.length > 0">
            <div class="chart-section" v-if="showPercentage">
                <div class="stacked-bar-wrapper">
                    <div class="stacked-bar percentage-bar" @mouseleave="activeIndex2 = null">
                        <div v-for="(score, index) in scoreList" :key="'score-' + index" class="bar-segment"
                            :class="{ active: activeIndex2 === index }"
                            :style="getSegmentStyle(score, totalScore, pctColors[index])"
                            @mouseenter="activeIndex2 = index">
                            <span class="segment-label" v-if="getSegmentWidth(score, totalScore) > 10">
                                {{ score.toFixed(1) }}/100.0
                            </span>
                        </div>
                    </div>
                </div>

                <!-- 悬浮提示框 -->
                <transition name="fade">
                    <div v-if="activeIndex2 !== null" class="tooltip tooltip-right">
                        <div class="tooltip-content">
                            <table>
                                <tbody>
                                    <tr v-for="(name, idx) in nameList" :key="'scoretip-' + idx">
                                        <td>
                                            <span class="color-dot" :style="{ backgroundColor: pctColors[idx] }"></span>
                                            {{ name }}
                                        </td>
                                        <td style="padding-left: 16px;">
                                            <span :class="{ highlight: idx === activeIndex2 }">{{
                                                scoreList[idx].toFixed(2) }}/100.0</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'SingleBar',
    props: {
        // 支柱名称列表
        nameList: {
            type: Array,
            required: true,
            validator: (val) => val.length > 0
        },
        // 总分列表
        totalList: {
            type: Array,
            default: () => []
        },
        // 排名列表（传入则显示排名条）
        rankList: {
            type: Array,
            default: () => []
        },
        // 得分列表（传入则显示得分条）
        scoreList: {
            type: Array,
            default: () => []
        },
        // 排名条颜色
        scoreColors: {
            type: Array,
            default: () => ['#5470c6', '#91cc75', '#fac858']
        },
        // 得分条颜色
        pctColors: {
            type: Array,
            default: () => ['#5470c6', '#91cc75', '#fac858']
        },
        // 是否显示得分条（默认true，当scoreList有值时自动显示）
        showPercentage: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            activeIndex: null,
            activeIndex2: null
        }
    },
    computed: {
        totalRank() {
            if (!this.rankList || this.rankList.length === 0) return 1
            return this.rankList.reduce((sum, val) => sum + val, 0)
        },
        totalScore() {
            if (!this.scoreList || this.scoreList.length === 0) return 1
            return this.scoreList.reduce((sum, val) => sum + val, 0)
        }
    },
    methods: {
        getSegmentStyle(value, total, color) {
            const pct = total > 0 ? (value / total) * 100 : 0
            return {
                width: pct + '%',
                backgroundColor: color,
                flexShrink: 0
            }
        },
        getSegmentWidth(value, total) {
            return total > 0 ? (value / total) * 100 : 0
        }
    }
}
</script>

<style scoped>
.pillar-charts-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.chart-section {
    position: relative;
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.chart-section:last-child {
    margin-bottom: 0;
}

.stacked-bar-wrapper {
    position: relative;
    width: 100%;
}

.stacked-bar {
    display: flex;
    width: 100%;
    height: 36px;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
    cursor: pointer;
}

.bar-segment {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    transition: all 0.3s ease;
    cursor: pointer;
    border-right: 2px solid #fff;
}

.bar-segment:last-child {
    border-right: none;
}

.bar-segment:hover,
.bar-segment.active {
    filter: brightness(1.1);
    transform: scaleY(1.08);
    z-index: 2;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.segment-label {
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    white-space: nowrap;
    pointer-events: none;
}

/* 悬浮提示框 */
.tooltip {
    position: absolute;
    top: 48px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.88);
    backdrop-filter: blur(8px);
    color: #fff;
    padding: 12px 16px;
    border-radius: 8px;
    min-width: 220px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    z-index: 100;
    pointer-events: none;
}

.tooltip-right {
    left: auto;
    right: 0;
    transform: none;
}

.tooltip::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: rgba(0, 0, 0, 0.88);
}

.tooltip-right::before {
    left: auto;
    right: 30px;
}

.tooltip-content table {
    width: 100%;
    border-collapse: collapse;
}

.tooltip-content td {
    padding: 4px 0;
    font-size: 13px;
    white-space: nowrap;
}

.color-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
}

.highlight {
    color: #ffd666;
    font-weight: 600;
}

/* 动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
}

.fade-enter-from.tooltip-right,
.fade-leave-to.tooltip-right {
    transform: translateY(-4px);
}

/* 响应式 */
@media (max-width: 640px) {
    .stacked-bar {
        height: 32px;
    }

    .segment-label {
        font-size: 11px;
    }

    .tooltip {
        min-width: 180px;
        padding: 10px 12px;
    }

    .tooltip-content td {
        font-size: 12px;
    }
}
</style>